import type { InferGetStaticPropsType, NextPage } from "next";
import colour from "public/images/TheColourOutOfSpaceArt.jpg";
import { useState } from "react";

import { CardCategoryEnum } from "@sorcery/db";
import {
  H2,
  H3,
  // InternalLink,
  Line,
  P,
  SplashImage,
  VariantSwitcher,
  useBreakpoints,
} from "@sorcery/ui";

import { VariantLabel } from "~/components/atoms/VariantLabel";
import { Page } from "~/components/layouts/Page";
import { FAQList } from "~/components/molecules/FAQList";
import { CardDisplayImage } from "~/components/molecules/card/CardDisplayImage";
import { CardFlipper } from "~/components/molecules/card/CardFlipper";
// import { CodexedText } from "~/components/molecules/cms/codex/CodexedText";
import { DeckPreviewList } from "~/components/molecules/deck/DeckPreviewList";
import type { VariantType } from "~/types/card";
// import { codexPath } from "~/utils/paths";
import { api } from "~/utils/trpc";
import type { getStaticProps } from "./index.page";

const Card: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  // const { data: codexes } = api.cms.searchCodexes.useQuery(
  //   {},
  //   {
  //     refetchOnMount: false,
  //     refetchOnReconnect: false,
  //     refetchOnWindowFocus: false,
  //   },
  // );

  const { is3xl, is2xl, isXl, isLg, isMd, isSm } = useBreakpoints();
  // available instantly due to SSR
  const { slug } = props;
  // available instantly on the client through ssr prefetching
  const { data: card } = api.card.getBySlug.useQuery(
    { slug },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  const { data: decks } = api.deck.getLatest.useQuery(
    {
      slug,
      count: is3xl ? 10 : is2xl ? 8 : isXl ? 8 : isLg ? 6 : isMd ? 4 : isSm ? 4 : 4,
    },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const [variant, setVariant] = useState<VariantType>(card?.variants[0]!);
  const hasReverse = !!card?.reverse;
  const [isReversed, setIsReversed] = useState(false);

  const toggleVariant = () => {
    setIsReversed(!isReversed);
  };

  const displayedVariant =
    isReversed && hasReverse
      ? // the no reverse on a reverse issue
        (variant?.reverse as unknown as VariantType)
      : variant;

  const isSite = card?.guardian?.category === CardCategoryEnum.Site;

  // const hardLinks =
  //   codexes?.filter((c) => c.hardLinks?.find((hl) => hl === card?.slug)) ?? [];

  if (card) {
    return (
      <>
        <SplashImage src={colour} fuckAll={true} />
        <Page
          title={card.name}
          description={card.variants[0]?.vMeta?.typeText}
          image={card.variants[0]?.src}
        >
          <div className="flex animate-in fade-in">
            <div className="relative mb-4 mr-8 hidden sm:block">
              <CardDisplayImage variant={displayedVariant} rotate={isSite} />
              {hasReverse && <CardFlipper onFlip={toggleVariant} />}
            </div>
            <div className="flex w-full max-w-[38rem] flex-col items-center sm:items-start">
              <H2 className="mb-2 w-full text-4xl sm:text-2xl lg:text-4xl">{card.name}</H2>
              <div key={displayedVariant.id} className="w-full duration-500 animate-in fade-in">
                <P className="!mb-2 italic">
                  {displayedVariant.vMeta?.typeText}
                  {/* {displayedVariant.vMeta!.typeText && (
                    <CodexedText
                      raw={displayedVariant.vMeta!.typeText}
                      codexes={codexes ?? []}
                      field={"type"}
                    />
                  )} */}
                </P>
                <P className="curiosa-quote !mb-2 opacity-80">
                  {displayedVariant.setCard.meta?.rulesText}
                  {/* {displayedVariant.setCard.meta!.rulesText && (
                    <CodexedText
                      raw={displayedVariant.setCard.meta!.rulesText}
                      codexes={codexes ?? []}
                      field={"rulesText"}
                    />
                  )} */}
                </P>
                <P className="!mb-2 opacity-80">{displayedVariant.artist.name}</P>
              </div>
              <div className="relative mb-6 w-full duration-500 animate-in fade-in sm:hidden">
                <Line className="mb-6 mt-4 w-full" />
                <div className="flex justify-center">
                  <CardDisplayImage variant={displayedVariant} rotate={isSite} />
                  {hasReverse && <CardFlipper onFlip={toggleVariant} />}
                </div>
              </div>

              <Line className="mb-4 mt-4 hidden w-full sm:block" />

              {card.variants.length > 1 ? (
                <VariantSwitcher
                  variant={variant}
                  variants={card.variants}
                  // biome-ignore lint/style/noNonNullAssertion: <explanation>
                  onChange={(v) => setVariant(card.variants.find((i) => i.id === v)!)}
                />
              ) : (
                <VariantLabel variant={displayedVariant} />
              )}

              <div className="block w-full sm:hidden">
                {/* {!!hardLinks.length && (
                  <>
                    <Line className="my-6" />
                    <div className="sm:max-h-[300px] sm:overflow-auto">
                      <H3>Codex</H3>
                      {hardLinks.map((c) => (
                        <div key={c._id} className="mb-4">
                          <InternalLink href={codexPath(c._id)}>
                            {c.title}
                          </InternalLink>
                        </div>
                      ))}
                    </div>
                  </>
                )} */}
                {!!card.faqs.length && (
                  <>
                    <Line className="my-6" />
                    <div className="sm:max-h-[300px] sm:overflow-auto">
                      <H3>FAQs</H3>
                      <FAQList faqs={card.faqs} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="hidden sm:block">
            {/* {!!hardLinks.length && (
              <div className="mb-8">
                <Line className="my-4" />
                <H3>Codex</H3>
                {hardLinks.map((c) => (
                  <div key={c._id} className="mb-4">
                    <InternalLink href={codexPath(c._id)}>
                      {c.title}
                    </InternalLink>
                  </div>
                ))}
              </div>
            )} */}
            {!!card.faqs.length && (
              <>
                <Line className="my-4" />
                <H3>FAQs</H3>
                <FAQList faqs={card.faqs} />
              </>
            )}
          </div>
          {!!decks && !!decks.length && (
            <>
              <Line className="my-6" />
              <H3 className="text-center sm:text-left">Latest {card.name} decks</H3>
              <DeckPreviewList decks={decks} align={isSm ? "left" : "center"} />
            </>
          )}
        </Page>
      </>
    );
  }
};

export default Card;
